import React, { useState } from "react"
import {
  manageMedicalRecordUpdate,
  manageMedicalRecordSensitivityUpdate
} from "../../../../api/manage_medical_record_update"
import { ManageDeviceData } from "../../device/"
import { MedicalRecordInfo } from "../../../tenant/components/TenantInfo/"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import { SENSITIVITY_OPTIONS, toSensitivityOption } from "../../../../constants/sensitivity"

type PeriodValue = string[] | null | undefined

interface ManageMedicalRecordUpdateProps {
  facilityId: string
  deviceData: ManageDeviceData[]
  deviceDataNow: ManageDeviceData[]
  devicePlace: string[]
  onClose: (num: number) => void
  initialData: MedicalRecordInfo
  sensitivityOnly?: boolean
}

const splitPeriod = (period?: PeriodValue) => {
  if (!period || period.length < 2) {
    return { start: "00:00", end: "00:00" }
  }

  return {
    start: period[0] || "00:00",
    end: period[1] || "00:00"
  }
}

const ManageMedicalRecordUpdate: React.FC<ManageMedicalRecordUpdateProps> = ({
  facilityId,
  deviceData,
  deviceDataNow,
  devicePlace,
  onClose,
  initialData,
  sensitivityOnly = false
}) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()

  const deviceIds: string[] = deviceDataNow.map((device) => device.id)
  const originallyIsPoc = initialData?.is_poc === 1

  const initialDetectionPeriod = splitPeriod(initialData.detection_period || initialData.effective_detection_period)
  const initialOptionPeriod = splitPeriod(initialData.option_period || initialData.effective_option_period)
  const initialSensitivityValue =
    initialData.sensitivity !== undefined && initialData.sensitivity !== null
      ? String(toSensitivityOption(initialData.sensitivity))
      : initialData.effective_sensitivity !== undefined && initialData.effective_sensitivity !== null
        ? String(toSensitivityOption(initialData.effective_sensitivity))
        : "96"

  const isTenantSettingEnabled = (enabled?: number | null, source?: string | null) => {
    // 明示的に 1 なら、入居者個別設定が有効
    if (enabled === 1) {
      return true
    }

    // 明示的に 0 なら、入居者個別設定として無効
    if (enabled === 0) {
      return false
    }

    // enabled が渡ってきていない場合でも、
    // 有効値の適用元が入居者個別ならONとして扱う
    return source === "medical_record"
  }

  const [formData, setFormData] = useState<{
    id: string
    room_no: string
    name: string
    initial: string
    birthday: string
    phone: string
    is_poc: boolean
    devices: string[]
    devicePlaces: string[]
    sensitivityEnabled: boolean
    sensitivity: string
    detectionEnabled: boolean
    detectionPeriodStart: string
    detectionPeriodEnd: string
    optionEnabled: boolean
    optionPeriodStart: string
    optionPeriodEnd: string
  }>({
    id: initialData.id?.toString() || "",
    room_no: initialData.room_no?.toString() || "",
    name: initialData.name || "",
    initial: initialData.initial || "",
    birthday: initialData.birthday?.toString() || "",
    phone: initialData.phone || "",
    is_poc: originallyIsPoc,
    devices: deviceIds,
    devicePlaces: initialData.device_places || [],

    sensitivityEnabled: isTenantSettingEnabled(
      initialData.sensitivity_enabled,
      initialData.effective_sensitivity_source
    ),
    sensitivity: initialSensitivityValue,

    detectionEnabled: isTenantSettingEnabled(
      initialData.detection_enabled,
      initialData.effective_detection_period_source
    ),
    detectionPeriodStart: initialDetectionPeriod.start,
    detectionPeriodEnd: initialDetectionPeriod.end,

    optionEnabled: isTenantSettingEnabled(initialData.option_enabled, initialData.effective_option_period_source),
    optionPeriodStart: initialOptionPeriod.start,
    optionPeriodEnd: initialOptionPeriod.end
  })

  const [errors, setErrors] = useState({
    room_no: "",
    name: "",
    initial: "",
    birthday: "",
    phone: "",
    sensitivity: "",
    detectionPeriod: "",
    optionPeriod: "",
    server: ""
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement
    const { name, value, type, checked } = target

    if (name.includes("devicePlace_")) {
      const index = parseInt(name.split("_")[1]) - 1
      setFormData((prev) => {
        const updatedDevicePlaces = [...prev.devicePlaces]
        updatedDevicePlaces[index] = value
        return { ...prev, devicePlaces: updatedDevicePlaces }
      })
      return
    }

    if (name.includes("device_")) {
      const index = parseInt(name.split("_")[1]) - 1
      setFormData((prev) => {
        const updatedDevices = [...prev.devices]
        updatedDevices[index] = value
        return { ...prev, devices: updatedDevices }
      })
      return
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const validate = () => {
    const validationErrors = {
      room_no: "",
      name: "",
      initial: "",
      birthday: "",
      phone: "",
      sensitivity: "",
      detectionPeriod: "",
      optionPeriod: "",
      server: ""
    }
    let hasError = false

    if (!sensitivityOnly) {
      if (!formData.room_no) {
        validationErrors.room_no = "必須項目が入力されていません"
        hasError = true
      } else if (formData.room_no.length > 5) {
        validationErrors.room_no = "入力文字数は5文字以内です"
        hasError = true
      }

      if (!formData.name) {
        validationErrors.name = "必須項目が入力されていません"
        hasError = true
      } else if (formData.name.length > 12) {
        validationErrors.name = "入力文字数は12文字以内です"
        hasError = true
      }

      if (!formData.initial) {
        validationErrors.initial = "必須項目が入力されていません"
        hasError = true
      } else if (formData.initial.length > 4) {
        validationErrors.initial = "入力文字数は4文字以内です"
        hasError = true
      }

      if (!formData.birthday) {
        validationErrors.birthday = "必須項目が入力されていません"
        hasError = true
      } else if (formData.birthday.length !== 6) {
        validationErrors.birthday = "生年月（YYYYMM）は6文字です"
        hasError = true
      }

      if (!formData.phone) {
        validationErrors.phone = "必須項目が入力されていません"
        hasError = true
      } else if (formData.phone.length > 14) {
        validationErrors.phone = "入力文字数は14文字以内です"
        hasError = true
      }
    }

    if (formData.sensitivityEnabled) {
      const sensitivity = Number(formData.sensitivity)
      if (formData.sensitivity === "" || Number.isNaN(sensitivity)) {
        validationErrors.sensitivity = "必須項目が入力されていません"
        hasError = true
      }
    }

    if (formData.detectionEnabled && (formData.detectionPeriodStart === "" || formData.detectionPeriodEnd === "")) {
      validationErrors.detectionPeriod = "必須項目が入力されていません"
      hasError = true
    }

    if (formData.optionEnabled && (formData.optionPeriodStart === "" || formData.optionPeriodEnd === "")) {
      validationErrors.optionPeriod = "必須項目が入力されていません"
      hasError = true
    }

    setErrors(validationErrors)
    return !hasError
  }

  const handleAddDevice = () => {
    if (formData.devices.length < 10) {
      setFormData((prev) => ({
        ...prev,
        devices: [...prev.devices, ""]
      }))
    }
  }

  const handleAddDevicePlace = () => {
    if (formData.devicePlaces.length < 10) {
      setFormData((prev) => ({
        ...prev,
        devicePlaces: [...prev.devicePlaces, ""]
      }))
    }
  }

  const handleRemoveDevice = (index: number) => {
    const newDevices = formData.devices.filter((_, i) => i !== index)
    setFormData({ ...formData, devices: newDevices })
  }

  const handleRemoveDevicePlace = (index: number) => {
    const newDevicePlaces = formData.devicePlaces.filter((_, i) => i !== index)
    setFormData({ ...formData, devicePlaces: newDevicePlaces })
  }

  const buildSensitivitySetting = () => {
    return {
      // チェックON: 入居者個別設定として有効
      // チェックOFF: 個別設定なしに戻し、サービス提供元設定を継承
      sensitivity_enabled: formData.sensitivityEnabled ? 1 : null,
      sensitivity: formData.sensitivityEnabled ? Number(formData.sensitivity) : null,

      detection_enabled: formData.detectionEnabled ? 1 : null,
      detection_period_start: formData.detectionEnabled ? formData.detectionPeriodStart : null,
      detection_period_end: formData.detectionEnabled ? formData.detectionPeriodEnd : null,

      option_enabled: formData.optionEnabled ? 1 : null,
      option_period_start: formData.optionEnabled ? formData.optionPeriodStart : null,
      option_period_end: formData.optionEnabled ? formData.optionPeriodEnd : null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      try {
        startLoading()

        if (sensitivityOnly) {
          await manageMedicalRecordSensitivityUpdate(initialData.id?.toString() || "", buildSensitivitySetting())
          onClose(1)
          return
        }

        const devices = formData.devices.concat(Array(10 - formData.devices.length).fill(null))
        const devicePlaces = formData.devicePlaces.concat(Array(10 - formData.devicePlaces.length).fill(null))

        await manageMedicalRecordUpdate(
          initialData.id?.toString() || "",
          "Facility|" + facilityId,
          formData.room_no,
          formData.name,
          formData.initial,
          parseInt(formData.birthday),
          formData.phone,
          formData.is_poc,
          devices[0],
          devices[1],
          devices[2],
          devices[3],
          devices[4],
          devices[5],
          devices[6],
          devices[7],
          devices[8],
          devices[9],
          devicePlaces[0],
          devicePlaces[1],
          devicePlaces[2],
          devicePlaces[3],
          devicePlaces[4],
          devicePlaces[5],
          devicePlaces[6],
          devicePlaces[7],
          devicePlaces[8],
          devicePlaces[9],
          buildSensitivitySetting()
        )
        onClose(1)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else if (error.response) {
          setErrors((prev) => ({ ...prev, server: error.response.data.detail.message }))
        } else {
          setErrors((prev) => ({ ...prev, server: "サーバーへの接続に失敗しました" }))
        }
      } finally {
        stopLoading()
      }
    }
  }

  const getAvailableDevices = (index: number) => {
    const selectedDevices = [...formData.devices]
    selectedDevices.splice(index, 1)
    return deviceData.filter((device) => !selectedDevices.includes(device.id))
  }

  const getAvailableDevicePlaces = (index: number) => {
    const selectedPlaces = [...formData.devicePlaces]
    selectedPlaces.splice(index, 1)
    return devicePlace.filter((place) => !selectedPlaces.includes(place))
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <button className="close-button" onClick={() => onClose(0)}>
          ×
        </button>
        <MDTypography variant="h4" fontWeight="medium">
          入居者を更新
        </MDTypography>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="居室ID"
              type="text"
              name="id"
              value={formData.id}
              onChange={handleChange}
              inputProps={{ readOnly: true }}
              sx={{ width: "100%" }}
            />
          </MDBox>

          {!sensitivityOnly && (
            <>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDInput
                  label="居室番号"
                  type="text"
                  name="room_no"
                  value={formData.room_no}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 99999, readOnly: sensitivityOnly }}
                  sx={{ width: "100%" }}
                />
              </MDBox>
              {errors.room_no && (
                <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                  {errors.room_no}
                </MDBox>
              )}

              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDInput
                  label="氏名"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 99999, readOnly: sensitivityOnly }}
                  sx={{ width: "100%" }}
                />
              </MDBox>
              {errors.name && (
                <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                  {errors.name}
                </MDBox>
              )}

              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDInput
                  label="イニシャル"
                  type="text"
                  name="initial"
                  value={formData.initial}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 99999, readOnly: sensitivityOnly }}
                  sx={{ width: "100%" }}
                />
              </MDBox>
              {errors.initial && (
                <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                  {errors.initial}
                </MDBox>
              )}

              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDInput
                  label="生年月（YYYYMM）"
                  type="text"
                  name="birthday"
                  value={formData.birthday}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 99999, readOnly: sensitivityOnly }}
                  sx={{ width: "100%" }}
                />
              </MDBox>
              {errors.birthday && (
                <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                  {errors.birthday}
                </MDBox>
              )}

              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDInput
                  label="電話番号"
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 99999, readOnly: sensitivityOnly }}
                  sx={{ width: "100%" }}
                />
              </MDBox>
              {errors.phone && (
                <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                  {errors.phone}
                </MDBox>
              )}
            </>
          )}

          {!sensitivityOnly && originallyIsPoc && (
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <FormControlLabel
                control={<Checkbox name="is_poc" checked={formData.is_poc} onChange={handleChange} />}
                label="PoC（お試し利用）設定"
              />
            </MDBox>
          )}
          {!sensitivityOnly && originallyIsPoc && !formData.is_poc && (
            <MDBox pl={4} mb={2}>
              <MDTypography variant="caption" color="error">
                ※チェックを外して更新すると、通常契約へ移行します。
              </MDTypography>
            </MDBox>
          )}
          {!sensitivityOnly && (
            <>
              <MDTypography display="flex" alignItems="center" mb={1} sx={{ fontSize: "0.75em" }}>
                デバイス
                <MDBox
                  display="flex"
                  gap={3}
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  sx={{ fontSize: "0.875rem", color: "text.primary", textAlign: "right", width: "calc(100% - 61px)" }}
                >
                  <MDBox>
                    <MDButton variant="gradient" color="info" size="medium" onClick={handleAddDevice}>
                      デバイス追加
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDTypography>
              {formData.devices.map((device, index) => (
                <MDBox
                  key={"device" + index}
                  display="flex"
                  gap={3}
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  sx={{ fontSize: "0.875rem", color: "text.primary", textAlign: "right", width: "100%" }}
                >
                  <select name={`device_${index + 1}`} value={device} onChange={handleChange}>
                    <option value="">選択してください</option>
                    {getAvailableDevices(index).map((deviceItem) => (
                      <option key={deviceItem.id} value={deviceItem.id}>
                        {deviceItem.serial_no}
                      </option>
                    ))}
                  </select>
                  <MDBox>
                    <MDButton variant="gradient" color="error" size="medium" onClick={() => handleRemoveDevice(index)}>
                      削除
                    </MDButton>
                  </MDBox>
                </MDBox>
              ))}

              <MDTypography display="flex" alignItems="center" mt={1} mb={1} sx={{ fontSize: "0.75em" }}>
                設置場所
                <MDBox
                  display="flex"
                  gap={3}
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  sx={{ fontSize: "0.875rem", color: "text.primary", textAlign: "right", width: "calc(100% - 61px)" }}
                >
                  <MDBox>
                    <MDButton variant="gradient" color="info" size="medium" onClick={handleAddDevicePlace}>
                      設置場所追加
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDTypography>
              {formData.devicePlaces.map((place, index) => (
                <MDBox
                  key={"devicePlace" + index}
                  display="flex"
                  gap={3}
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  sx={{ fontSize: "0.875rem", color: "text.primary", textAlign: "right", width: "100%" }}
                >
                  <select name={`devicePlace_${index + 1}`} value={place} onChange={handleChange}>
                    <option value="">設置場所を選択</option>
                    {getAvailableDevicePlaces(index).map((placeItem, i) => (
                      <option key={i} value={placeItem}>
                        {placeItem}
                      </option>
                    ))}
                  </select>
                  <MDBox>
                    <MDButton
                      variant="gradient"
                      color="error"
                      size="medium"
                      onClick={() => handleRemoveDevicePlace(index)}
                    >
                      削除
                    </MDButton>
                  </MDBox>
                </MDBox>
              ))}
            </>
          )}

          <MDBox mt={3} mb={1}>
            <MDTypography variant="h6">感度設定</MDTypography>
            <MDTypography variant="caption" color="text">
              チェックを入れると入居者個別設定を使用します。チェックを外すとサービス提供元設定を継承します。
            </MDTypography>
          </MDBox>

          <MDBox mt={2}>
            <FormControlLabel
              control={
                <Checkbox name="sensitivityEnabled" checked={formData.sensitivityEnabled} onChange={handleChange} />
              }
              label="感度設定を有効化"
            />
            <MDInput
              select
              SelectProps={{ native: true }}
              label="感度"
              name="sensitivity"
              value={formData.sensitivity}
              onChange={handleChange}
              disabled={!formData.sensitivityEnabled}
              sx={{ width: "100%" }}
            >
              {SENSITIVITY_OPTIONS.map((option) => (
                <option key={option.value} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </MDInput>
            {errors.sensitivity && (
              <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                {errors.sensitivity}
              </MDBox>
            )}
          </MDBox>

          <MDBox mt={2}>
            <FormControlLabel
              control={<Checkbox name="detectionEnabled" checked={formData.detectionEnabled} onChange={handleChange} />}
              label="検知時間帯を有効化"
            />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDInput
                type="time"
                name="detectionPeriodStart"
                value={formData.detectionPeriodStart}
                onChange={handleChange}
                disabled={!formData.detectionEnabled}
              />
              〜
              <MDInput
                type="time"
                name="detectionPeriodEnd"
                value={formData.detectionPeriodEnd}
                onChange={handleChange}
                disabled={!formData.detectionEnabled}
              />
            </MDBox>
            {errors.detectionPeriod && (
              <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                {errors.detectionPeriod}
              </MDBox>
            )}
          </MDBox>

          <MDBox mt={2}>
            <FormControlLabel
              control={<Checkbox name="optionEnabled" checked={formData.optionEnabled} onChange={handleChange} />}
              label="検知時間帯（オプション）を有効化"
            />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDInput
                type="time"
                name="optionPeriodStart"
                value={formData.optionPeriodStart}
                onChange={handleChange}
                disabled={!formData.optionEnabled}
              />
              〜
              <MDInput
                type="time"
                name="optionPeriodEnd"
                value={formData.optionPeriodEnd}
                onChange={handleChange}
                disabled={!formData.optionEnabled}
              />
            </MDBox>
            {errors.optionPeriod && (
              <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
                {errors.optionPeriod}
              </MDBox>
            )}
          </MDBox>

          {errors.server && (
            <MDBox color="#FF0000" pl={2} mb={2} fontSize="0.7em">
              {errors.server}
            </MDBox>
          )}

          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              更新
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageMedicalRecordUpdate
